/**
 * RC-152c-1 + RC-152c-2a + RC-152c-2a-style — Form Designer page.
 *
 * Standalone authoring surface for a form_task's formSchema.
 * Opens at /forms/design?wid=<workflowId>&nid=<formTaskNodeId>;
 * Canvas form_task Inspector's "Design form ↗" button is the only
 * authored navigation site.
 *
 * <p><b>Visual contract (RC-152c-2a-style operator directive,
 * 2026-06-21):</b></p>
 * <ul>
 *   <li>Page chrome is provided by {@link V2PageShell} — same shell
 *       every other v2 page uses (Trust Center, Compliance Center,
 *       etc.). NO hand-rolled padding / background / max-width.</li>
 *   <li>Surfaces are {@link Card} composites. NO inline border /
 *       background / shadow style objects.</li>
 *   <li>Controls are {@link Button} / {@link Input} / {@link Label}
 *       / {@link Toast} / {@link EmptyState} from the v2 design
 *       system. NO hand-rolled buttons or banners.</li>
 *   <li>Layout-only Tailwind utilities (flex/grid/gap/etc.) are
 *       allowed in className. Tokenized utilities
 *       ({@code bg-v2-*}, {@code text-v2-*}, {@code border-v2-*})
 *       are encouraged over inline hex / inline var(--*).</li>
 * </ul>
 *
 * <p><b>Zero canvas-runtime coupling.</b> The designer reads + writes
 * the same {@code {fields:[…]}} JSON shape the runtime + Approval
 * Center already consume. {@code CanvasDAGWorkflowImpl}, the
 * variable-substitution layer (RC-149), Conductor adapters — none
 * touched.</p>
 *
 * <p><b>Zero new BE surface.</b> Save uses the existing
 * {@code PUT /api/v1/workflows/{id}/canvas} endpoint with the
 * existing {@code If-Match} contract. Concurrent canvas-side
 * edits trigger the same 412 path the canvas itself handles.</p>
 */

import React, { useEffect, useState, useCallback, useReducer, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AlertCircle, FileText, Save, RefreshCw, Eye, Pencil, UserCheck, ChevronDown, ChevronRight } from 'lucide-react';
import { getWorkflow, saveCanvas } from '../../services/api';
import {
  V2PageShell,
  Button,
  Card,
  Toast,
  EmptyState,
  Pill,
  AssigneePicker,
  Input,
  Select,
  FieldGroup,
  Label,
} from '../../components/v2';
import useActiveScope from '../../hooks/useActiveScope';
import FieldPalette from './FieldPalette';
import FormCanvas from './FormCanvas';
import FieldInspector from './FieldInspector';
import { DEFAULTS_BY_TYPE, nextFieldName } from './_shared/fieldTypes';

/**
 * RC-156g — default Assignment & Deadline shape. Used when the operator
 * opens a form_task that doesn't yet have an assignment block, so the
 * UI renders with sensible starting values instead of empty controls.
 *
 * The shape mirrors what FormTaskLifecycleWorkflowImpl (RC-156d) reads at
 * runtime; keep both sides in sync when adding fields. See
 * FORM_INGESTION_MODEL.md for the contract.
 */
const DEFAULT_ASSIGNMENT = {
  mode: 'native_assignee',  // 'native_assignee' (Shape B) | 'external_api' (Shape A — future RC-155)
  assignee: { assigneeType: 'USER', assigneeRef: {} },
  deadline: 'now+24h',  // Relative expression OR absolute ISO datetime; reuses RC-152c-2a-datetz semantics
  escalation: {
    enabled: false,
    escalateEveryHours: 24,
    maxRounds: 3,
    escalateTo: { assigneeType: 'USER', assigneeRef: {} },
  },
  onExpiry: 'halt_workflow', // 'halt_workflow' | 'auto_continue' | 'notify_only'
  reminders: [],            // fractions of deadline (e.g. [0.5, 0.8])
};

/** Read assignment block from node.config.assignment; fall back to defaults
 *  for legacy form_task nodes that pre-date RC-156. */
function readAssignment(node) {
  const cfg = node && node.config ? node.config.assignment : null;
  if (!cfg) return { ...DEFAULT_ASSIGNMENT };
  return {
    mode: cfg.mode || DEFAULT_ASSIGNMENT.mode,
    assignee: cfg.assignee || { ...DEFAULT_ASSIGNMENT.assignee },
    deadline: cfg.deadline || DEFAULT_ASSIGNMENT.deadline,
    escalation: {
      enabled: !!(cfg.escalation && cfg.escalation.enabled),
      escalateEveryHours: (cfg.escalation && cfg.escalation.escalateEveryHours) || 24,
      maxRounds: (cfg.escalation && cfg.escalation.maxRounds) || 3,
      escalateTo: (cfg.escalation && cfg.escalation.escalateTo) || { assigneeType: 'USER', assigneeRef: {} },
    },
    onExpiry: cfg.onExpiry || DEFAULT_ASSIGNMENT.onExpiry,
    reminders: Array.isArray(cfg.reminders) ? cfg.reminders : [],
  };
}

/** Find the form_task / human_task node with the given id. */
function findFormTaskNode(definition, nodeId) {
  if (!definition || !Array.isArray(definition.nodes)) return null;
  return definition.nodes.find((n) => n && n.id === nodeId) || null;
}

/** Read fields[] out of a node's config.formSchema; tolerate
 *  string-shaped schemas (legacy or hand-edited). */
function readFields(node) {
  const raw = node && node.config ? node.config.formSchema : null;
  if (!raw) return [];
  let schema = raw;
  if (typeof raw === 'string') {
    try { schema = JSON.parse(raw); } catch { return []; }
  }
  if (!schema || !Array.isArray(schema.fields)) return [];
  // Stamp a stable per-field _uid so React keys + reorder behave
  // correctly even when fields lose their `name` mid-edit.
  return schema.fields.map((f, i) => ({
    _uid: f._uid || `f_${i}_${Math.random().toString(36).slice(2, 8)}`,
    ...f,
  }));
}

/** Composition state reducer — single source of truth for the
 *  edited fields list + the selected-field index + RC-156g assignment block. */
function composeReducer(state, action) {
  switch (action.type) {
    case 'INIT':
      return {
        fields: action.fields,
        selectedIndex: action.fields.length > 0 ? 0 : null,
        assignment: action.assignment || { ...DEFAULT_ASSIGNMENT },
        dirty: false,
      };
    case 'UPDATE_ASSIGNMENT':
      // Shallow merge so callers can patch a single key
      // (e.g. { onExpiry: 'auto_continue' }) without rebuilding the whole block.
      return { ...state, assignment: { ...state.assignment, ...action.patch }, dirty: true };
    case 'UPDATE_ASSIGNMENT_ESCALATION':
      // Nested escalation patch — same shallow merge but under .escalation.
      return {
        ...state,
        assignment: {
          ...state.assignment,
          escalation: { ...state.assignment.escalation, ...action.patch },
        },
        dirty: true,
      };
    case 'APPEND_FIELD': {
      const fields = [...state.fields, action.field];
      return { ...state, fields, selectedIndex: fields.length - 1, dirty: true };
    }
    case 'UPDATE_FIELD': {
      const fields = state.fields.map((f, i) => (i === action.index ? action.next : f));
      return { ...state, fields, dirty: true };
    }
    case 'MOVE_FIELD': {
      const { index, delta } = action;
      const nextIdx = index + delta;
      if (nextIdx < 0 || nextIdx >= state.fields.length) return state;
      const fields = state.fields.slice();
      const [moved] = fields.splice(index, 1);
      fields.splice(nextIdx, 0, moved);
      const selectedIndex = state.selectedIndex === index ? nextIdx : state.selectedIndex;
      return { ...state, fields, selectedIndex, dirty: true };
    }
    case 'REMOVE_FIELD': {
      const fields = state.fields.filter((_, i) => i !== action.index);
      // After removal, clamp selectedIndex into the new range or
      // clear it when the list became empty.
      let selectedIndex = state.selectedIndex;
      if (fields.length === 0) selectedIndex = null;
      else if (selectedIndex != null && selectedIndex >= fields.length) selectedIndex = fields.length - 1;
      return { ...state, fields, selectedIndex, dirty: true };
    }
    case 'SELECT_FIELD':
      return { ...state, selectedIndex: action.index };
    case 'MARK_CLEAN':
      return { ...state, dirty: false };
    default:
      return state;
  }
}

export default function FormDesignerPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // RC-156g — active tenant scope drives AssigneePicker's candidate
  // fetch (USER / GROUP / ROLE lists are tenant-scoped per FORM_INGESTION
  // _MODEL.md decision #4). Sourced from JWT via useActiveScope (the
  // RC-137a aggregate) so a tenant switch invalidates cleanly.
  const activeScope = useActiveScope();

  const workflowId = params.get('wid');
  const nodeId = params.get('nid');

  const [loading, setLoading] = useState(Boolean(workflowId));
  const [error, setError] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveOk, setSaveOk] = useState(null);     // success-toast message
  const [conflict, setConflict] = useState(null); // 412 banner state
  const [mode, setMode] = useState('design');     // 'design' | 'preview'

  // Loaded-at version anchor for the If-Match header on save.
  const versionRef = useRef(null);

  const [state, dispatch] = useReducer(composeReducer, { fields: [], selectedIndex: null, dirty: false });

  // ──────────────────────────────────────────────────────────────────
  // Load workflow + initialise composition from the form_task node.
  // ──────────────────────────────────────────────────────────────────
  const loadWorkflow = useCallback(async () => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);
    setConflict(null);
    try {
      const res = await getWorkflow(workflowId);
      if (res && res.ok === false) {
        setError(res.error || 'Could not load the workflow.');
        setWorkflow(null);
        return;
      }
      const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      setWorkflow(payload || null);
      versionRef.current = payload && payload.version != null ? payload.version : null;
      const node = findFormTaskNode(payload && payload.definition, nodeId);
      dispatch({
        type: 'INIT',
        fields: readFields(node),
        assignment: readAssignment(node),
      });
    } catch (e) {
      setError(e?.message || 'Could not load the workflow.');
      setWorkflow(null);
    } finally {
      setLoading(false);
    }
  }, [workflowId, nodeId]);

  useEffect(() => { loadWorkflow(); }, [loadWorkflow]);

  const node = workflow && nodeId ? findFormTaskNode(workflow.definition, nodeId) : null;

  // ──────────────────────────────────────────────────────────────────
  // Editor actions — append / update / move / remove / select.
  // ──────────────────────────────────────────────────────────────────
  const handleAppendField = (type) => {
    const defaults = DEFAULTS_BY_TYPE[type];
    if (!defaults) return;
    const field = {
      _uid: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      ...defaults,
      name: nextFieldName(type, state.fields),
    };
    dispatch({ type: 'APPEND_FIELD', field });
  };
  const handleUpdateField = (index, next) => dispatch({ type: 'UPDATE_FIELD', index, next });
  const handleMoveField   = (index, delta) => dispatch({ type: 'MOVE_FIELD', index, delta });
  const handleRemoveField = (index) => dispatch({ type: 'REMOVE_FIELD', index });
  const handleSelectField = (index) => dispatch({ type: 'SELECT_FIELD', index });

  // ──────────────────────────────────────────────────────────────────
  // Save — clones the workflow definition, replaces the form_task's
  // formSchema with the current composition, PUTs via saveCanvas
  // with If-Match for optimistic-lock.
  //
  // On 412 the conflict toast asks the operator to reload. The full
  // "Save anyway" path (force write with If-Match: *) is the
  // upcoming Form-Designer iteration that extracts the shared
  // conflict renderer from CanvasPageV3.
  // ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!workflow || !node) return;
    setSaving(true);
    setSaveError(null);
    setSaveOk(null);
    setConflict(null);

    // Strip the per-field _uid before persisting — it's a local
    // React-key concern, not a schema concern.
    const cleanedFields = state.fields.map(({ _uid, ...rest }) => rest);

    const def = JSON.parse(JSON.stringify(workflow.definition || {}));
    const nodes = Array.isArray(def.nodes) ? def.nodes : [];
    const idx = nodes.findIndex((n) => n && n.id === nodeId);
    if (idx < 0) {
      setSaveError('Could not find the form node in the workflow definition. Please reload.');
      setSaving(false);
      return;
    }
    const updatedNode = { ...nodes[idx] };
    // RC-156g — persist the Assignment & Deadline block alongside
    // formSchema. The runtime (RC-156d/f) reads config.assignment to drive
    // the FormTaskLifecycleWorkflowImpl child workflow. When mode is the
    // legacy 'approval_center' (or absent), the old approval-gate path
    // still runs unchanged — config.assignment is ignored.
    updatedNode.config = {
      ...(updatedNode.config || {}),
      formSchema: { fields: cleanedFields },
      assignment: state.assignment,
    };
    nodes[idx] = updatedNode;
    def.nodes = nodes;

    try {
      const res = await saveCanvas(workflowId, def, {
        changeKind: 'manual_save',
        ifMatchVersion: versionRef.current,
      });
      if (res && (res.status === 412 || (res.data && res.data.code === 'workflow_version_conflict'))) {
        const body = res.data || {};
        setConflict({
          currentVersion: body.currentVersion ?? null,
          attemptedVersion: body.attemptedVersion ?? versionRef.current,
          message: body.error || 'This workflow was modified elsewhere since you opened the designer.',
        });
        return;
      }
      if (!res || !res.ok) {
        setSaveError((res && (res.error || (res.data && res.data.error))) || 'Save failed.');
        return;
      }
      const newVersion = res.data && res.data.version != null
        ? res.data.version
        : (versionRef.current != null ? versionRef.current + 1 : null);
      versionRef.current = newVersion;
      dispatch({ type: 'MARK_CLEAN' });
      setSaveOk(`Form saved. Version v${newVersion ?? '?'}.`);
      setWorkflow((w) => (w ? { ...w, version: newVersion, definition: def } : w));
    } catch (e) {
      setSaveError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const backTo = workflowId
    ? `/canvas?workflowId=${encodeURIComponent(workflowId)}`
    : '/workflows';

  // ──────────────────────────────────────────────────────────────────
  // Header pieces for V2PageShell — Mode toggle + Save action.
  // ──────────────────────────────────────────────────────────────────
  const headerActions = node ? (
    <>
      <div role="group" aria-label="Editor mode" className="inline-flex">
        <Button
          variant={mode === 'design' ? 'primary' : 'secondary'}
          size="sm"
          iconLeft={<Pencil />}
          onClick={() => setMode('design')}
        >
          Design
        </Button>
        <span className="inline-block w-1" />
        <Button
          variant={mode === 'preview' ? 'primary' : 'secondary'}
          size="sm"
          iconLeft={<Eye />}
          onClick={() => setMode('preview')}
        >
          Preview
        </Button>
      </div>
      <Button
        variant="primary"
        iconLeft={<Save />}
        onClick={handleSave}
        loading={saving}
        disabled={!state.dirty}
        ariaLabel={state.dirty ? 'Save form schema' : 'No changes to save'}
      >
        {saving ? 'Saving' : 'Save'}
      </Button>
    </>
  ) : null;

  const headerTags = node ? (
    <>
      <Pill tone="muted">form_task</Pill>
      {state.dirty && <Pill tone="warning">Unsaved changes</Pill>}
    </>
  ) : null;

  const title = node ? (node.label || node.id) : 'Form Designer';
  const subtitle = node && workflow
    ? `${workflow.name || 'Workflow'} · v${workflow.version ?? '?'}`
    : 'Design the form rendered for this approval step.';

  return (
    <V2PageShell
      title={title}
      subtitle={subtitle}
      tags={headerTags}
      actions={headerActions}
      backTo={backTo}
      backLabel={workflowId ? 'Back to canvas' : 'Back to workflows'}
    >
      {/* Save / conflict feedback — V2 Toast primitive (title +
        * description + optional action slot). Default 4s auto-dismiss
        * for success; duration={0} for the conflict toast so the
        * operator has time to read the Reload action. */}
      {saveOk && (
        <div className="mb-4">
          <Toast
            tone="success"
            title="Form saved"
            description={saveOk}
            onDismiss={() => setSaveOk(null)}
          />
        </div>
      )}
      {saveError && (
        <div className="mb-4">
          <Toast
            tone="danger"
            title="Save failed"
            description={saveError}
            duration={0}
            onDismiss={() => setSaveError(null)}
          />
        </div>
      )}
      {conflict && (
        <div className="mb-4">
          <Toast
            tone="warning"
            title="Version conflict"
            description={
              <>
                {conflict.message}
                {conflict.currentVersion != null && (
                  <span className="ml-1.5 text-[11px] text-v2-ink-muted">
                    (current v{conflict.currentVersion}, your attempt v{conflict.attemptedVersion ?? '?'})
                  </span>
                )}
              </>
            }
            action={
              <Button variant="secondary" size="sm" iconLeft={<RefreshCw />} onClick={loadWorkflow}>
                Reload
              </Button>
            }
            duration={0}
            onDismiss={() => setConflict(null)}
          />
        </div>
      )}

      {/* Empty / loading / error states — EmptyState primitive */}
      {!workflowId && (
        <Card>
          <Card.Body>
            <EmptyState
              icon={<FileText />}
              title="No form selected"
              description="Open a workflow on the canvas, click a form_task node, and use the 'Design form ↗' button in its inspector."
              primaryAction={
                <Link to="/workflows">
                  <Button variant="primary">Go to Workflows</Button>
                </Link>
              }
            />
          </Card.Body>
        </Card>
      )}
      {workflowId && loading && (
        <Card>
          <Card.Body>
            <div className="text-[13px] text-v2-ink-secondary py-6 text-center">Loading workflow…</div>
          </Card.Body>
        </Card>
      )}
      {workflowId && !loading && error && (
        <Card>
          <Card.Body>
            <EmptyState
              icon={<AlertCircle />}
              title="Could not load the workflow"
              description={error}
              primaryAction={<Button variant="primary" onClick={loadWorkflow}>Retry</Button>}
            />
          </Card.Body>
        </Card>
      )}
      {workflowId && !loading && !error && workflow && !node && (
        <Card>
          <Card.Body>
            <EmptyState
              icon={<AlertCircle />}
              title="Form node not found"
              description={`The workflow loaded, but no node with id "${nodeId || '(missing)'}" exists in its current definition.`}
              primaryAction={
                <Button variant="primary" onClick={() => navigate(backTo)}>Open in canvas</Button>
              }
            />
          </Card.Body>
        </Card>
      )}

      {/* RC-156g — ASSIGNMENT & DEADLINE — collapsible Card sibling to
        * the composition editor. Operator-chosen assignment captures
        * (a) who fills the form, (b) by when, (c) what happens if
        * they don't. The runtime (RC-156d/f) reads this block to drive
        * the FormTaskLifecycleWorkflowImpl child workflow. */}
      {workflowId && !loading && !error && node && mode === 'design' && (
        <div className="mb-4">
          <AssignmentSection
            assignment={state.assignment}
            tenantId={activeScope?.tenantId}
            onPatch={(patch) => dispatch({ type: 'UPDATE_ASSIGNMENT', patch })}
            onPatchEscalation={(patch) => dispatch({ type: 'UPDATE_ASSIGNMENT_ESCALATION', patch })}
          />
        </div>
      )}

      {/* DESIGN MODE — three-column editor inside a Card. Grid columns
        * are layout-only Tailwind utilities, no inline width style. */}
      {workflowId && !loading && !error && node && mode === 'design' && (
        <Card padding="none">
          <div className="grid grid-cols-[220px_1fr_320px] min-h-[640px]">
            <FieldPalette onAppendField={handleAppendField} />
            <FormCanvas
              fields={state.fields}
              selectedIndex={state.selectedIndex}
              onSelectField={handleSelectField}
              onMoveField={handleMoveField}
              onRemoveField={handleRemoveField}
            />
            <FieldInspector
              field={state.selectedIndex != null ? state.fields[state.selectedIndex] : null}
              fieldIndex={state.selectedIndex}
              onUpdateField={handleUpdateField}
            />
          </div>
        </Card>
      )}

      {/* PREVIEW MODE — read-only preview card */}
      {workflowId && !loading && !error && node && mode === 'preview' && (
        <Card>
          <Card.Header
            title="Preview"
            subtitle="How the form will appear in the Approval Center."
          />
          <Card.Body>
            <SimplePreview fields={state.fields} />
          </Card.Body>
        </Card>
      )}
    </V2PageShell>
  );
}

// ──────────────────────────────────────────────────────────────────
// Simple read-only preview pane.
// A future iteration extracts the shared FormSchemaRenderer from
// ApprovalCenter so designer preview becomes byte-identical to the
// approver render. The renderer below is a Tailwind-only stub —
// no inline style objects with sizing/colors, only layout utilities.
// ──────────────────────────────────────────────────────────────────
function SimplePreview({ fields }) {
  if (!fields || fields.length === 0) {
    return (
      <div className="text-[12px] text-v2-ink-muted italic py-8 text-center">
        No fields to preview yet — add a field from the palette in Design mode.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {fields.map((field, i) => {
        const ft = (field.type || 'text').toLowerCase();
        const required = !!field.required;
        return (
          <div key={field._uid || i}>
            <div className="text-[12px] text-v2-ink-secondary mb-1 flex items-center gap-1.5">
              <span>{field.label || '(unlabeled)'}</span>
              {required && <span className="text-v2-brand">*</span>}
              <span className="text-[10.5px] text-v2-ink-muted">· {ft}</span>
            </div>
            <PreviewInput field={field} />
            {field.helpText && (
              <div className="text-[11px] text-v2-ink-muted mt-1">{field.helpText}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// AssignmentSection (RC-156g) — Assignment & Deadline configuration.
//
// Renders as a collapsible Card so it doesn't crowd the composition
// editor for operators who haven't yet rolled out Shape B in their org.
// Defaults to COLLAPSED on first render; expansion is per-page-load
// (no persistence) — the editor remembers via React state only.
//
// All controls compose existing V2 primitives + the RC-157f
// AssigneePicker. NO inline style={{}} with hardcoded sizes —
// per feedback_v2_primitives_no_inline_style.
// ──────────────────────────────────────────────────────────────────
function AssignmentSection({ assignment, tenantId, onPatch, onPatchEscalation }) {
  const [expanded, setExpanded] = React.useState(false);
  const ChevronIcon = expanded ? ChevronDown : ChevronRight;

  return (
    <Card padding="md">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="v2-focus-ring w-full text-left flex items-center gap-2 -m-1 p-1 rounded-v2-sm"
        aria-expanded={expanded}
      >
        <ChevronIcon size={16} aria-hidden="true" className="text-v2-ink-secondary" />
        <UserCheck size={16} aria-hidden="true" className="text-v2-action" />
        <span className="text-[13px] font-semibold text-v2-ink">Assignment &amp; deadline</span>
        <span className="text-[11.5px] text-v2-ink-muted ml-2">
          {summarizeAssignment(assignment)}
        </span>
      </button>

      {expanded && (
        <div className="mt-4 flex flex-col gap-4">
          {/* Assignee picker — USER/GROUP/ROLE tab toggle */}
          <AssigneePicker
            tenantId={tenantId}
            value={assignment.assignee}
            onChange={(next) => onPatch({ assignee: next })}
            label="Assignee"
            hint="Who is responsible for filling out this form. For GROUP / ROLE assignments any member of the resolved set can submit."
            required
          />

          {/* Deadline — RC-213: preset Select + Custom datetime picker.
              Replaces the prior free-text Input that accepted "now+24h"
              / ISO strings verbatim — too freeform, error-prone, and
              the operator had no affordance to pick an absolute date.
              See DeadlinePicker below for the resolution semantics. */}
          <FieldGroup
            label="Deadline"
            hint="Pick a preset or choose a specific date and time. Existing custom strings (non-preset) are preserved and shown in the date picker when parseable."
          >
            <DeadlinePicker
              value={assignment.deadline || ''}
              onChange={(v) => onPatch({ deadline: v })}
            />
          </FieldGroup>

          {/* On-expiry behavior */}
          <FieldGroup
            label="On expiry"
            hint="What happens when the deadline passes without a submission AND all escalation rounds have fired."
          >
            <Select
              size="sm"
              value={assignment.onExpiry || 'halt_workflow'}
              onChange={(e) => onPatch({ onExpiry: e.target.value })}
            >
              <option value="halt_workflow">Halt workflow (parent run fails)</option>
              <option value="auto_continue">Auto-continue (pass empty form values + continue)</option>
              <option value="notify_only">Notify only (task stays visible as expired)</option>
            </Select>
          </FieldGroup>

          {/* Escalation policy — collapsed sub-block when disabled */}
          <div>
            <Label>Escalation</Label>
            <label className="flex items-center gap-2 text-[12.5px] text-v2-ink cursor-pointer">
              <input
                type="checkbox"
                checked={!!assignment.escalation?.enabled}
                onChange={(e) => onPatchEscalation({ enabled: e.target.checked })}
              />
              Escalate when the deadline is missed
            </label>

            {assignment.escalation?.enabled && (
              <div className="mt-3 flex flex-col gap-3 pl-6 border-l-2 border-v2-border-subtle">
                <div className="grid grid-cols-2 gap-3">
                  <FieldGroup label="Check every (hours)" hint="How often the lifecycle workflow re-evaluates and notifies.">
                    <Input
                      size="sm"
                      type="number"
                      value={assignment.escalation?.escalateEveryHours ?? 24}
                      onChange={(e) => onPatchEscalation({ escalateEveryHours: Number(e.target.value) })}
                      min="1"
                    />
                  </FieldGroup>
                  <FieldGroup label="Max rounds" hint="After this many escalation cycles the task expires.">
                    <Input
                      size="sm"
                      type="number"
                      value={assignment.escalation?.maxRounds ?? 3}
                      onChange={(e) => onPatchEscalation({ maxRounds: Number(e.target.value) })}
                      min="1"
                    />
                  </FieldGroup>
                </div>
                <AssigneePicker
                  tenantId={tenantId}
                  value={assignment.escalation?.escalateTo}
                  onChange={(next) => onPatchEscalation({ escalateTo: next })}
                  label="Escalate to"
                  hint="The user / group / role notified at each escalation round."
                />
              </div>
            )}
          </div>

          {/* Reminders — at fractions of the deadline */}
          <FieldGroup
            label="Reminders"
            hint="Send a reminder before the deadline. Multi-select; values are fractions of the deadline."
          >
            <div className="flex items-center gap-4 mt-1">
              {[0.5, 0.8].map((frac) => {
                const checked = (assignment.reminders || []).includes(frac);
                return (
                  <label key={frac} className="flex items-center gap-1.5 text-[12.5px] text-v2-ink cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = new Set(assignment.reminders || []);
                        if (e.target.checked) next.add(frac);
                        else next.delete(frac);
                        onPatch({ reminders: Array.from(next).sort() });
                      }}
                    />
                    At {Math.round(frac * 100)}% of deadline
                  </label>
                );
              })}
            </div>
          </FieldGroup>
        </div>
      )}
    </Card>
  );
}

/** Tiny inline summary for the collapsed Assignment & Deadline card —
 *  shows the operator the gist without having to expand. */
function summarizeAssignment(a) {
  if (!a) return '';
  const parts = [];
  const t = a.assignee?.assigneeType;
  const ref = a.assignee?.assigneeRef || {};
  if (t === 'USER' && ref.userId) parts.push('User');
  else if (t === 'GROUP' && ref.groupId) parts.push('Group');
  else if (t === 'ROLE' && ref.roleName) parts.push(`Role: ${ref.roleName}`);
  else parts.push('Assignee not set');
  if (a.deadline) parts.push(`due ${a.deadline}`);
  if (a.escalation?.enabled) parts.push(`escalates ×${a.escalation.maxRounds ?? 3}`);
  return parts.join(' · ');
}

/*
 * ──────────────────────────────────────────────────────────────
 * RC-213 — DeadlinePicker
 * ──────────────────────────────────────────────────────────────
 *
 * Replaces the prior free-text deadline Input with a structured
 * preset Select + Custom datetime affordance.
 *
 * Storage shape — UNCHANGED from prior implementation. The runtime
 * (CanvasDAGWorkflowImpl.resolveDeadlineIso) already accepts both
 *   - relative strings: "now+24h", "now+3d", "now+30m"
 *   - absolute ISO: "2026-12-31T17:00:00-05:00"
 * so the BE doesn't need any change. This is a pure FE UX upgrade.
 *
 * Mode resolution:
 *   1. value is empty/null            → preset mode, defaults to "now+24h"
 *   2. value matches a known preset   → preset mode, Select shows that preset
 *   3. value parses as a Date         → custom mode, datetime-local pre-filled
 *   4. value is non-preset non-Date   → custom mode with empty datetime
 *                                       + raw value shown below as fallback text
 *                                       (rare — legacy free-text entries)
 *
 * Datetime conversion:
 *   - datetime-local input value (YYYY-MM-DDTHH:mm in browser-local tz)
 *     is converted to ISO via Date.toISOString() (UTC) for storage.
 *   - On read, ISO → datetime-local format via local-tz formatting.
 *   - This preserves the absolute instant the operator picked, displayed
 *     in their local zone in the picker. Same convention as
 *     RC-152c-2a-datetz for field-level date/datetime fields.
 */
const DEADLINE_PRESETS = [
  { value: 'now+1h',  label: '1 hour' },
  { value: 'now+4h',  label: '4 hours' },
  { value: 'now+24h', label: '24 hours (1 day)' },
  { value: 'now+3d',  label: '3 days' },
  { value: 'now+7d',  label: '7 days (1 week)' },
  { value: 'now+14d', label: '14 days (2 weeks)' },
  { value: 'now+30d', label: '30 days' },
];
const DEADLINE_CUSTOM_VALUE = '__custom__';
const DEADLINE_PRESET_VALUES = new Set(DEADLINE_PRESETS.map((p) => p.value));

function DeadlinePicker({ value, onChange }) {
  const isPreset = !value || DEADLINE_PRESET_VALUES.has(value);
  const selectValue = isPreset ? (value || 'now+24h') : DEADLINE_CUSTOM_VALUE;

  // For custom mode: try to convert stored value to datetime-local format.
  // If value is a parseable Date, pre-fill the picker. If it's a non-preset
  // relative string (e.g. legacy "now+5h"), leave picker empty and surface
  // the raw value below so the operator can see what was saved before.
  const customDatetimeLocal = !isPreset ? isoToDatetimeLocal(value) : '';
  const showLegacyRawFallback =
    !isPreset && !customDatetimeLocal && value;

  const handleSelectChange = (e) => {
    const v = e.target.value;
    if (v === DEADLINE_CUSTOM_VALUE) {
      // Switching INTO custom: clear preset so the datetime-local input
      // surfaces empty (operator picks fresh). If the current value is
      // already a parseable Date, preserve it (don't accidentally drop
      // a valid custom datetime when re-selecting Custom from preset).
      if (isPreset) onChange('');
      // else keep value as-is
    } else {
      onChange(v);
    }
  };

  const handleDatetimeChange = (e) => {
    const v = e.target.value;  // YYYY-MM-DDTHH:mm in browser-local tz
    if (!v) { onChange(''); return; }
    const localDate = new Date(v);
    if (!Number.isNaN(localDate.valueOf())) {
      onChange(localDate.toISOString());
    } else {
      // Browser's datetime-local should never produce an invalid value,
      // but defensive: pass through raw if it does.
      onChange(v);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Select
        size="sm"
        value={selectValue}
        onChange={handleSelectChange}
        aria-label="Deadline preset"
      >
        {DEADLINE_PRESETS.map((p) => (
          <option key={p.value} value={p.value}>{p.label}</option>
        ))}
        <option value={DEADLINE_CUSTOM_VALUE}>Custom date and time…</option>
      </Select>
      {selectValue === DEADLINE_CUSTOM_VALUE && (
        <>
          <Input
            size="sm"
            type="datetime-local"
            value={customDatetimeLocal}
            onChange={handleDatetimeChange}
            aria-label="Custom deadline date and time"
          />
          {showLegacyRawFallback && (
            <div className="text-[11px] text-v2-ink-muted italic">
              Saved value: <span className="font-mono">{value}</span>
              {' '}— not a recognised preset or date. Pick a new date above to replace it.
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Convert an ISO datetime string to the YYYY-MM-DDTHH:mm format the
 * browser's datetime-local input requires (always in local timezone).
 * Returns '' for null / unparseable inputs so the input renders empty.
 */
function isoToDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
       + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function PreviewInput({ field }) {
  const ft = (field.type || 'text').toLowerCase();
  // baseCls intentionally minimal — Tailwind classes against V2 tokens,
  // matching the look of the Input primitive without re-importing it
  // (we want the preview to render in a disabled posture).
  const baseCls = 'w-full text-[12.5px] px-3 h-8 bg-v2-muted text-v2-ink-muted border border-v2-border rounded-v2-md';
  if (ft === 'textarea') {
    return <textarea disabled rows={field.rows || 4} placeholder={field.placeholder || ''}
      className="w-full text-[12.5px] px-3 py-2 bg-v2-muted text-v2-ink-muted border border-v2-border rounded-v2-md resize-y" />;
  }
  if (ft === 'select') {
    const opts = Array.isArray(field.options) ? field.options : [];
    return (
      <select disabled className={baseCls}>
        <option>Select…</option>
        {opts.map((o, i) => <option key={i}>{(o && o.label) || (o && o.value) || String(o)}</option>)}
      </select>
    );
  }
  if (ft === 'multi-select' || ft === 'multiselect') {
    const opts = Array.isArray(field.options) ? field.options : [];
    return (
      <div className="flex flex-col gap-1">
        {opts.length === 0 && <div className="text-[11px] text-v2-ink-muted italic">No options configured</div>}
        {opts.map((o, i) => (
          <label key={i} className="flex items-center gap-1.5 text-[12.5px] text-v2-ink-secondary">
            <input type="checkbox" disabled /> {(o && o.label) || (o && o.value) || String(o)}
          </label>
        ))}
      </div>
    );
  }
  if (ft === 'checkbox' || ft === 'boolean') {
    return (
      <label className="flex items-center gap-1.5 text-[12.5px] text-v2-ink-secondary">
        <input type="checkbox" disabled /> {field.label || field.name}
      </label>
    );
  }
  if (ft === 'file') {
    const accepted = Array.isArray(field.acceptedTypes) ? field.acceptedTypes.join(' / ') : 'any';
    const maxBytes = field.maxSizeBytes != null ? ` · max ${Math.round(field.maxSizeBytes / 1024)} KB` : '';
    return (
      <div className="w-full text-[12.5px] px-3 py-3 bg-v2-muted text-v2-ink-muted border border-dashed border-v2-border rounded-v2-md text-center">
        Drop a file · {accepted}{maxBytes}
      </div>
    );
  }
  if (ft === 'number') return <input type="number" disabled placeholder={field.placeholder || ''} className={baseCls} />;
  if (ft === 'date') return <input type="date" disabled className={baseCls} />;
  if (ft === 'datetime' || ft === 'datetime-local') return <input type="datetime-local" disabled className={baseCls} />;
  return <input type="text" disabled placeholder={field.placeholder || ''} className={baseCls} />;
}
